import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useUI } from '../../context/UIContext';
import { useUser } from '../../context/UserContext';
import Sidebar from './Sidebar';
import ArtifactPanel from './ArtifactPanel';
import { KeyboardShortcutsModal, CommandPalette, SettingsModal, PromptLibraryModal, FeatureTour, useFeatureTour } from '../ui';
// Footer removed from Layout — copyright shown on login page and sidebar BuildInfo only
import { useSwipeToReveal } from '../../hooks';
import { csrfFetch } from '../../utils/csrfFetch';

/**
 * Layout - Main three-column layout wrapper
 *
 * Structure:
 * - Left: Sidebar (conversations list, collapsible, resizable)
 * - Center: Main content area (children)
 * - Right: Artifact panel (slides in/out, resizable)
 *
 * Responsive behavior:
 * - Desktop (>= 1024px): All three columns visible
 * - Tablet (768-1023px): Sidebar overlay, main + artifact
 * - Mobile (< 768px): Single column, panels as overlays
 *
 * @param {React.ReactNode} children - Main content to display
 * @param {boolean} showSidebar - Whether to show sidebar (default true)
 * @param {boolean} showArtifactPanel - Whether artifact panel can be shown (default true)
 */
function Layout({ children, showSidebar = true, showArtifactPanel = true }) {
  const {
    sidebarOpen,
    toggleSidebar,
    artifactPanelOpen,
    isMobile,
    setIsMobile
  } = useUI();

  // Keyboard shortcuts modal state (SAT-77)
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Command palette state (SAT-83)
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Feature tour state (SAT-85)
  const { isOpen: showFeatureTour, startTour, closeTour } = useFeatureTour();

  // Settings modal state (for command palette)
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Prompt library modal state (for command palette)
  const [showPromptLibraryModal, setShowPromptLibraryModal] = useState(false);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsMobile]);

  // Swipe gesture support for mobile sidebar (SAT-91)
  const { swipeHandlers } = useSwipeToReveal({
    enabled: isMobile && showSidebar,
    edgeWidth: 30, // 30px edge zone for swipe-to-reveal
    onReveal: () => {
      if (!sidebarOpen) {
        toggleSidebar();
      }
    },
    onHide: () => {
      if (sidebarOpen) {
        toggleSidebar();
      }
    },
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in inputs
      const isInput = e.target.tagName === 'INPUT' ||
                      e.target.tagName === 'TEXTAREA' ||
                      e.target.isContentEditable;

      // Cmd/Ctrl + B: Toggle sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }

      // ? key: Open keyboard shortcuts (SAT-77)
      // Only trigger if not in an input and shift is held (for ?)
      if (e.key === '?' && !isInput) {
        e.preventDefault();
        setShowKeyboardShortcuts(true);
      }

      // Cmd/Ctrl + K: Open command palette (SAT-83)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // Command palette action handlers (SAT-83)
  const handleOpenSettings = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const handleOpenKeyboardShortcuts = useCallback(() => {
    setShowKeyboardShortcuts(true);
  }, []);

  const handleOpenPromptLibrary = useCallback(() => {
    setShowPromptLibraryModal(true);
  }, []);

  // Feature tour handler (SAT-85)
  const handleStartFeatureTour = useCallback(() => {
    startTour();
  }, [startTour]);

  return (
    <div
      className="flex h-screen overflow-hidden bg-white dark:bg-gray-900 touch-pan-y"
      {...swipeHandlers}
    >
      {/* Skip to main content link (SAT-87 Accessibility) */}
      <a
        href="#main-content"
        className="skip-to-content"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById('main-content');
          if (main) {
            main.focus();
            main.scrollIntoView();
          }
        }}
      >
        Skip to main content
      </a>

      {/* Mobile sidebar toggle button - fixed position when sidebar is closed (SAT-82 touch-friendly) */}
      {isMobile && !sidebarOpen && showSidebar && (
        <button
          onClick={toggleSidebar}
          className="fixed top-3 left-3 z-50
            min-w-[44px] min-h-[44px] p-2.5
            flex items-center justify-center
            bg-white dark:bg-gray-800
            border border-gray-200 dark:border-gray-700
            rounded-xl shadow-lg
            text-gray-600 dark:text-gray-300
            hover:bg-gray-100 dark:hover:bg-gray-700
            active:scale-95
            transition-all duration-150"
          aria-label="Open sidebar"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Mobile overlay backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden modal-backdrop-transition animate-fadeIn"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Left Sidebar */}
      {showSidebar && (
        <div className={`
          ${isMobile ? 'fixed inset-y-0 left-0 z-40' : 'relative h-full'}
          ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}
          sidebar-transition gpu-accelerated
        `}>
          <Sidebar />
        </div>
      )}

      {/* Desktop expand button - shown when sidebar is collapsed */}
      {showSidebar && !sidebarOpen && !isMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed top-1/2 -translate-y-1/2 left-0 z-40
            w-5 h-16
            flex items-center justify-center
            bg-gray-200 dark:bg-gray-700
            hover:bg-gray-300 dark:hover:bg-gray-600
            border border-l-0 border-gray-300 dark:border-gray-600
            rounded-r-lg
            text-gray-500 dark:text-gray-400
            hover:text-gray-700 dark:hover:text-gray-200
            transition-colors shadow-xs"
          aria-label="Open sidebar"
          title="Open sidebar"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Main Content Area */}
      <main
        id="main-content"
        tabIndex="-1"
        className="flex-1 min-w-0 overflow-hidden focus:outline-hidden flex flex-col" style={{ position: 'relative' }}
        role="main"
        aria-label="Main content"
      >
        <SkillUpdateBanner />
        <div className="flex-1 min-h-0 flex flex-col">
          {children}
        </div>
      </main>

      {/* Right Artifact Panel */}
      {showArtifactPanel && artifactPanelOpen && (
        <ArtifactPanel />
      )}

      {/* Keyboard Shortcuts Modal (SAT-77) */}
      <KeyboardShortcutsModal
        isOpen={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* Command Palette (SAT-83) */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onOpenSettings={handleOpenSettings}
        onOpenKeyboardShortcuts={handleOpenKeyboardShortcuts}
        onOpenPromptLibrary={handleOpenPromptLibrary}
        onStartFeatureTour={handleStartFeatureTour}
      />

      {/* Settings Modal (for command palette) */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Prompt Library Modal (for command palette) */}
      <PromptLibraryModal
        isOpen={showPromptLibraryModal}
        onClose={() => setShowPromptLibraryModal(false)}
      />

      {/* Feature Tour (SAT-85) */}
      <FeatureTour
        isOpen={showFeatureTour}
        onClose={closeTour}
      />
    </div>
  );
}

/**
 * SkillUpdateBanner (ADR-005 P5) — checks for upstream skill updates on session start.
 * Shows a dismissible amber banner when overridden skills have newer upstream versions.
 * Checks once per session (sessionStorage flag prevents repeated API calls).
 */
function SkillUpdateBanner() {
  const { isAdmin } = useUser();
  const [updates, setUpdates] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only check for admins, and only once per session
    if (!isAdmin || !isAdmin() || sessionStorage.getItem('skillUpdateChecked')) return;

    sessionStorage.setItem('skillUpdateChecked', 'true');
    csrfFetch('/api/platform/overrides/updates', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { updates: [] })
      .then(data => {
        if (data.updates && data.updates.length > 0) setUpdates(data.updates);
      })
      .catch(() => {});
  }, [isAdmin]);

  if (dismissed || updates.length === 0) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2 flex items-center gap-3">
      <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
      <p className="flex-1 text-sm text-amber-800 dark:text-amber-300">
        <strong>{updates.length} skill{updates.length > 1 ? 's have' : ' has'} upstream updates.</strong>{' '}
        Your custom versions are active.{' '}
        <a href="/admin/skills" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-200">
          Review updates
        </a>
      </p>
      <button onClick={() => setDismissed(true)} className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default Layout;
